// The church treasury's own income: tithes, general offerings, special offerings and other income,
// counted after each Sabbath service, and the money passed on (tithe to the Conference and so on).
// Money is ALWAYS integer ngwee here. Everything is written through database functions that only the
// church treasurer can call; church-wide readers (the treasurer and auditors) can read it all and a
// department can read none of it (migration 0017).
import { supabase } from '@/lib/supabase'
import { FriendlyError } from './auth'
import { DataError, unwrap } from './errors'
import type { PaymentMethod } from './dues'

export type IncomeKind = 'tithe' | 'offering' | 'special_offering' | 'other'
export type IncomeDestination = 'local' | 'passed_on'

export const KIND_LABELS: Record<IncomeKind, string> = {
  tithe: 'Tithe',
  offering: 'Offering',
  special_offering: 'Special offering',
  other: 'Other income',
}
export const DESTINATION_LABELS: Record<IncomeDestination, string> = {
  local: 'Stays with the church',
  passed_on: 'Passed on (e.g. to the Conference)',
}

// ---------------------------------------------------------------- funds
export interface IncomeFund {
  id: string
  name: string
  kind: IncomeKind
  destination: IncomeDestination
  sortOrder: number
  archived: boolean
}
type FundRow = { id: string; name: string; kind: IncomeKind; destination: IncomeDestination; sort_order: number; archived_at: string | null }
const fund = (r: FundRow): IncomeFund => ({
  id: r.id,
  name: r.name,
  kind: r.kind,
  destination: r.destination,
  sortOrder: Number(r.sort_order),
  archived: r.archived_at !== null,
})

/** Every fund, in the order the admin set. Retired ones are included (marked), so old counts still name them. */
export async function listFunds(): Promise<IncomeFund[]> {
  const res = await supabase.from('income_fund').select('id, name, kind, destination, sort_order, archived_at').order('sort_order').order('name')
  return (unwrap(res as never, 'Could not load the funds.') as FundRow[]).map(fund)
}

export interface FundInput {
  name: string
  /** Not 'tithe': there is exactly one tithe fund and it already exists. */
  kind: Exclude<IncomeKind, 'tithe'>
  destination: IncomeDestination
}

export async function createFund(input: FundInput): Promise<void> {
  const name = input.name.trim()
  if (name.length < 2) throw new FriendlyError('Give the fund a name of at least two letters.')
  if ((input.kind as string) === 'tithe') throw new FriendlyError('There is already a tithe fund.')
  const res = await supabase.from('income_fund').insert({ name, kind: input.kind, destination: input.destination })
  if (res.error) {
    if (res.error.code === '23505') throw new FriendlyError('There is already a fund with that name.')
    throw new DataError(res.error, 'Could not add the fund.')
  }
}

export async function renameFund(id: string, name: string): Promise<void> {
  const clean = name.trim()
  if (clean.length < 2) throw new FriendlyError('Give the fund a name of at least two letters.')
  const res = await supabase.from('income_fund').update({ name: clean }).eq('id', id).select('id')
  if (res.error) {
    if (res.error.code === '23505') throw new FriendlyError('There is already a fund with that name.')
    throw new DataError(res.error, 'Could not rename the fund.')
  }
  if (((res.data as unknown[]) ?? []).length === 0) throw new FriendlyError('That fund could not be found.')
}

/** Retiring a fund only hides it from NEW counts; its history stays. The tithe fund cannot be retired. */
export async function setFundArchived(id: string, archived: boolean): Promise<void> {
  const res = await supabase
    .from('income_fund')
    .update({ archived_at: archived ? new Date().toISOString() : null })
    .eq('id', id)
    .select('id')
  if (res.error) throw new DataError(res.error, 'Could not change the fund.')
  if (((res.data as unknown[]) ?? []).length === 0) throw new FriendlyError('That fund could not be found.')
}

// ---------------------------------------------------------------- balances and totals
export interface FundBalance {
  fundId: string
  name: string
  kind: IncomeKind
  destination: IncomeDestination
  archived: boolean
  receivedNgwee: number
  sentNgwee: number
  /** Passed-on funds only: received - sent. Negative means more was sent than has been counted. */
  heldNgwee: number
}
type BalanceRow = {
  fund_id: string
  name: string
  kind: IncomeKind
  destination: IncomeDestination
  archived_at: string | null
  received_ngwee: number
  sent_ngwee: number
  held_ngwee: number
}

export async function fundBalances(): Promise<FundBalance[]> {
  const res = await supabase
    .from('v_income_fund_balance')
    .select('fund_id, name, kind, destination, archived_at, received_ngwee, sent_ngwee, held_ngwee')
    .order('sort_order')
    .order('name')
  return (unwrap(res as never, 'Could not load the fund balances.') as BalanceRow[]).map((r) => ({
    fundId: r.fund_id,
    name: r.name,
    kind: r.kind,
    destination: r.destination,
    archived: r.archived_at !== null,
    receivedNgwee: Number(r.received_ngwee),
    sentNgwee: Number(r.sent_ngwee),
    heldNgwee: Number(r.held_ngwee),
  }))
}

export interface QuarterIncome {
  fundId: string
  fundName: string
  kind: IncomeKind
  destination: IncomeDestination
  totalNgwee: number
}
type QuarterRow = { fund_id: string; fund_name: string; kind: IncomeKind; destination: IncomeDestination; total_ngwee: number }

/** Net income per fund for one quarter ('2026-Q3'). A reversal counts in the quarter it was made. */
export async function quarterIncome(quarterId: string): Promise<QuarterIncome[]> {
  const res = await supabase.from('v_income_quarter_totals').select('fund_id, fund_name, kind, destination, total_ngwee').eq('quarter_id', quarterId).order('fund_name')
  return (unwrap(res as never, 'Could not load the income for that quarter.') as QuarterRow[]).map((r) => ({
    fundId: r.fund_id,
    fundName: r.fund_name,
    kind: r.kind,
    destination: r.destination,
    totalNgwee: Number(r.total_ngwee),
  }))
}

// ---------------------------------------------------------------- counted sessions
export interface IncomeSession {
  id: string
  serviceOn: string
  quarterId: string
  label: string | null
  counter1: string
  counter2: string
  note: string | null
  /** Net of any reversals. */
  totalNgwee: number
  lineCount: number
  /** Count-sheet photos that finished uploading. */
  photoCount: number
  createdAt: string
}
type SessionRow = {
  session_id: string
  service_on: string
  quarter_id: string
  label: string | null
  counter_1: string
  counter_2: string
  note: string | null
  total_ngwee: number
  line_count: number
  proof_count: number
  created_at: string
}

/** Counted sessions, newest Sabbath first (the last 100). */
export async function listSessions(): Promise<IncomeSession[]> {
  const res = await supabase
    .from('v_income_session_summary')
    .select('session_id, service_on, quarter_id, label, counter_1, counter_2, note, total_ngwee, line_count, proof_count, created_at')
    .order('service_on', { ascending: false })
    .order('created_at', { ascending: false })
    .limit(100)
  return (unwrap(res as never, 'Could not load the counted services.') as SessionRow[]).map((r) => ({
    id: r.session_id,
    serviceOn: r.service_on,
    quarterId: r.quarter_id,
    label: r.label,
    counter1: r.counter_1,
    counter2: r.counter_2,
    note: r.note,
    totalNgwee: Number(r.total_ngwee),
    lineCount: Number(r.line_count),
    photoCount: Number(r.proof_count),
    createdAt: r.created_at,
  }))
}

export interface IncomeLine {
  id: string
  sessionId: string
  fundId: string
  fundName: string
  fundKind: IncomeKind
  amountNgwee: number
  method: PaymentMethod
  reference: string | null
  /** The Sabbath for a normal line; the day it was made for a reversal. */
  effectiveOn: string
  status: 'posted' | 'reversed'
  /** Set on a reversing row: the line it undoes. */
  reversesId: string | null
  reversalReason: string | null
}
type LineRow = {
  id: string
  session_id: string
  fund_id: string
  amount_ngwee: number
  method: PaymentMethod
  reference: string | null
  effective_on: string
  status: 'posted' | 'reversed'
  reverses_id: string | null
  reversal_reason: string | null
  fund: { name: string; kind: IncomeKind } | null
}

/** Every line of one session, reversals included, in the order they were made. */
export async function listSessionLines(sessionId: string): Promise<IncomeLine[]> {
  const res = await supabase
    .from('income_line')
    .select('id, session_id, fund_id, amount_ngwee, method, reference, effective_on, status, reverses_id, reversal_reason, fund:fund_id(name, kind)')
    .eq('session_id', sessionId)
    .order('created_at')
    .order('id')
  // All the lines of one count are saved in one moment, so they share a timestamp. Show them in a
  // steady order (by fund, then payment method) rather than whatever order the database returns.
  const rows = (unwrap(res as never, 'Could not load that count.') as unknown as LineRow[])
    .slice()
    .sort((a, b) => (a.reverses_id ? 1 : 0) - (b.reverses_id ? 1 : 0) || (a.fund?.name ?? '').localeCompare(b.fund?.name ?? '') || a.method.localeCompare(b.method))
  return rows.map((r) => ({
    id: r.id,
    sessionId: r.session_id,
    fundId: r.fund_id,
    fundName: r.fund?.name ?? 'Unknown fund',
    fundKind: r.fund?.kind ?? 'other',
    amountNgwee: Number(r.amount_ngwee),
    method: r.method,
    reference: r.reference,
    effectiveOn: r.effective_on,
    status: r.status,
    reversesId: r.reverses_id,
    reversalReason: r.reversal_reason,
  }))
}

export interface SessionLineInput {
  fundId: string
  amountNgwee: number
  method: PaymentMethod
  reference?: string
}
export interface SessionInput {
  serviceOn: string
  counter1: string
  counter2: string
  label?: string
  note?: string
  lines: SessionLineInput[]
}

const isDay = (s: string) => /^\d{4}-\d{2}-\d{2}$/.test(s)

/**
 * Save a counted Sabbath with all its amounts, or nothing. `id` is made by the screen once per form and
 * reused on a retry, so a request whose answer was lost finds the count it already made. The same id
 * with different details, or the same details under a new id, is refused by the database.
 */
export async function recordSession(input: SessionInput, id: string): Promise<string> {
  if (!isDay(input.serviceOn)) throw new FriendlyError('Choose the date of the service.')
  const c1 = input.counter1.trim()
  const c2 = input.counter2.trim()
  if (c1.length < 2 || c2.length < 2) throw new FriendlyError('Enter the names of both people who counted.')
  if (c1.toLowerCase() === c2.toLowerCase()) throw new FriendlyError('The two counters must be two different people.')
  if (input.lines.length === 0) throw new FriendlyError('Add at least one amount.')
  const seen = new Set<string>()
  for (const l of input.lines) {
    if (!l.fundId) throw new FriendlyError('Choose a fund for every amount.')
    if (!Number.isSafeInteger(l.amountNgwee) || l.amountNgwee <= 0) throw new FriendlyError('Every amount must be more than zero.')
    const key = `${l.fundId}|${l.method}`
    if (seen.has(key)) throw new FriendlyError('The same fund and payment method appears twice. Combine them into one amount.')
    seen.add(key)
  }
  const res = await supabase.rpc('record_income_session', {
    p_id: id,
    p_service_on: input.serviceOn,
    p_counter_1: c1,
    p_counter_2: c2,
    p_lines: input.lines.map((l) => ({
      fund_id: l.fundId,
      amount_ngwee: l.amountNgwee,
      method: l.method,
      reference: l.reference?.trim() ? l.reference.trim() : null,
    })),
    p_label: input.label?.trim() ? input.label.trim() : null,
    p_note: input.note?.trim() ? input.note.trim() : null,
  })
  return unwrap(res as never, 'Could not save the count.') as string
}

/** Undo one amount with a visible, dated reversing entry. The original stays on record. */
export async function reverseLine(lineId: string, reason: string): Promise<void> {
  if (!reason.trim()) throw new FriendlyError('Say why this is being reversed.')
  const res = await supabase.rpc('reverse_income_line', { p_id: lineId, p_reason: reason.trim() })
  if (res.error) throw new DataError(res.error, 'Could not reverse that amount.')
}

// ---------------------------------------------------------------- money passed on
export interface IncomeTransfer {
  id: string
  fundId: string
  fundName: string
  amountNgwee: number
  method: PaymentMethod
  sentOn: string
  recipient: string
  reference: string | null
  note: string | null
  status: 'posted' | 'reversed'
  reversesId: string | null
  reversalReason: string | null
  /** Slip photos that finished uploading. */
  photoCount: number
}
type TransferRow = {
  id: string
  fund_id: string
  amount_ngwee: number
  method: PaymentMethod
  sent_on: string
  recipient: string
  reference: string | null
  note: string | null
  status: 'posted' | 'reversed'
  reverses_id: string | null
  reversal_reason: string | null
  fund: { name: string } | null
}

/** Money sent on, newest first (the last 100), optionally for one fund. */
export async function listTransfers(fundId?: string): Promise<IncomeTransfer[]> {
  let q = supabase
    .from('income_transfer')
    .select('id, fund_id, amount_ngwee, method, sent_on, recipient, reference, note, status, reverses_id, reversal_reason, fund:fund_id(name)')
    .order('sent_on', { ascending: false })
    .order('created_at', { ascending: false })
    .limit(100)
  if (fundId) q = q.eq('fund_id', fundId)
  const rows = unwrap((await q) as never, 'Could not load the money sent on.') as unknown as TransferRow[]
  if (rows.length === 0) return []
  const photos = unwrap(
    (await supabase.from('attachment').select('transfer_id').eq('status', 'ready').in('transfer_id', rows.map((r) => r.id))) as never,
    'Could not load the slip photos.',
  ) as { transfer_id: string }[]
  const counts = new Map<string, number>()
  for (const p of photos) counts.set(p.transfer_id, (counts.get(p.transfer_id) ?? 0) + 1)
  return rows.map((r) => ({
    id: r.id,
    fundId: r.fund_id,
    fundName: r.fund?.name ?? 'Unknown fund',
    amountNgwee: Number(r.amount_ngwee),
    method: r.method,
    sentOn: r.sent_on,
    recipient: r.recipient,
    reference: r.reference,
    note: r.note,
    status: r.status,
    reversesId: r.reverses_id,
    reversalReason: r.reversal_reason,
    photoCount: counts.get(r.id) ?? 0,
  }))
}

export interface TransferInput {
  fundId: string
  amountNgwee: number
  method: PaymentMethod
  sentOn: string
  recipient?: string
  reference?: string
  note?: string
}

/** Record money sent on from a passed-on fund. `id` is made by the screen once per form. */
export async function recordTransfer(input: TransferInput, id: string): Promise<string> {
  if (!input.fundId) throw new FriendlyError('Choose which fund this was sent from.')
  if (!Number.isSafeInteger(input.amountNgwee) || input.amountNgwee <= 0) throw new FriendlyError('Enter an amount greater than zero.')
  if (!isDay(input.sentOn)) throw new FriendlyError('Choose the date it was sent.')
  const res = await supabase.rpc('record_income_transfer', {
    p_id: id,
    p_fund_id: input.fundId,
    p_amount_ngwee: input.amountNgwee,
    p_method: input.method,
    p_sent_on: input.sentOn,
    p_recipient: input.recipient?.trim() || 'Conference',
    p_reference: input.reference?.trim() || null,
    p_note: input.note?.trim() || null,
  })
  return unwrap(res as never, 'Could not record the transfer.') as string
}

export async function reverseTransfer(transferId: string, reason: string): Promise<void> {
  if (!reason.trim()) throw new FriendlyError('Say why this is being reversed.')
  const res = await supabase.rpc('reverse_income_transfer', { p_id: transferId, p_reason: reason.trim() })
  if (res.error) throw new DataError(res.error, 'Could not reverse that transfer.')
}
